import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {PageBackgroundOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-background-option';
import {PageTypeOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-type-option';
import {ContextualMenuHeadingOption} from '@Components/contextual-menu/components/contextual-menu-heading-option';
import {PageGridOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-grid-option';
import {PageFoldsOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-folds-option';
import {PageBleedOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-bleed-option';
import {DesignNameOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/design-name-option';
import {PosterEditorDesignSubPanels} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-default-options/poster-editing-default-options.types';
import {ContextualMenuHeader} from '@Components/contextual-menu/components/contextual-menu-header';
import {ContextualMenuTextHeader} from '@Components/contextual-menu/components/contextual-menu-text-header';
import {ContextualMenu} from '@Components/contextual-menu';
import {PageBackgroundColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-background-color-panel';
import {SuperUserPosterControls} from '@Components/poster-editor/components/poster-editing-side-panel/components/super-user-poster-controls/super-user-poster-controls';
import {ImageBackgroundOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/image-background-options';
import {VideoBackgroundOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/video-background-options';
import {ImageBgTintColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/image-bg-tint-color-panel';
import {ImageBgMultiplyColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/image-bg-multiply-color-panel';
import {VideoBgTintColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/video-bg-tint-color-panel';
import {VideoBgMultiplyColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/video-bg-multiply-color-panel';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {PageUnlockAllItemsOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-unlock-all-items-option';
import {DesignerAttribution} from '@Components/poster-editor/components/poster-editing-side-panel/components/designer-attribution';
import {AiSubtitleState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import AiSubtitlePanel from '@Panels/ai-subtitle-panel/ai-subtitle-panel';
import {useAppSelector} from '@/hooks';
import styles from './poster-editing-default-options.module.scss';
import {PosterGenerateAiSubtitlesOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/generate-ai-subtitles-option/components/poster-generate-ai-subtitles-option/poster-generate-ai-subtitles-option';
import {AiRedesignPromptBox} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box';
import type {EditableComponentsMap, SelectedPromptOptionValues} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {EditableComponent, MoodType, PromptSuggestions} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {PageAnimationOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-animation-option/page-animation-option';
import {PageAlignmentGuidesOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-alignment-guides-option/page-alignment-guides-options';
import {ContextualMenuContentKeys} from '@Components/poster-editor/components/poster-editing-side-panel/components/contextual-options/contextual-options.types';
import {PILL_CONTENT_TYPE} from '@Components/pill';
import {TextSize} from '@Components/text';
import {ColorPaletteStylesOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/color-palette-styles-option/color-palette-styles-option';
import {PageColorPaletteStylesPanelContent} from '@Components/poster-editor/components/page-color-palette-styles-panel-content/page-color-palette-styles-panel-content';
import {useGetNumberOfPages} from '@Hooks/poster-editor/useGetNumberOfPages';
import {useGetCurrentPageOrder} from '@Hooks/poster-editor/useGetCurrentPageOrder';
import {useGetCurrentPageBackgroundType} from '@Hooks/poster-editor/useGetCurrentPageBackgroundType';
import {useGetTeamCredits} from '@Hooks/useGetTeamCredits';
import {
  calculateAiRedesignCost,
  getEditableComponentsMap,
  getHeaderContent,
  getPromptBoxPreviousPrompt,
  getPromptOptionsForEditableComponents,
  onAiRedesignContextualMenuOpened,
  onAiRedesignIconClicked,
  onAiRedesignPromptOptionChanged,
  onAiRedesignPromptSuggestionSelected,
  onAiRedesignRecentPromptSelected,
  regenerateContent,
  saveEditableComponentsMapToStorage,
} from '@Components/poster-editor/library/poster-editor-ai-redesign-library';
import {isUserPremium} from '@Libraries/user.library';
import {BackgroundTypeName} from '@PosterWhiteboard/page/background/background.types';
import {PageAnimationPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-animation-panel/page-animation-panel';

function PosterEditingDefaultOptions(): ReactElement<any> {
  const [currentlyOpenedSubPanel, setCurrentlyOpenedSubPanel] = useState(PosterEditorDesignSubPanels.NONE);
  const [redesignCost, setRedesignCost] = useState<number>(() => calculateAiRedesignCost(getEditableComponentsMap()));
  const totalNumberOfPages = useGetNumberOfPages();
  const currentPageOrder = useGetCurrentPageOrder();
  const currentPageId = useAppSelector((state) => {
    return state.posterEditor.currentPageId;
  });
  const currentPageBackground = window.posterEditor?.whiteboard?.pages.pagesHashMap[currentPageId ?? '']?.background;
  const currentPageBackgroundType = useGetCurrentPageBackgroundType();
  const aiSubtitleState = useAppSelector((state) => {
    return state.posterEditor.aiState.aiSubtitles;
  });

  const teamCredits = useGetTeamCredits();
  const remainingCredits = teamCredits ?? 0;

  const onEditableComponentsMapChange = (editableComponentsMap: EditableComponentsMap): void => {
    saveEditableComponentsMapToStorage(editableComponentsMap);

    const updatedCost = calculateAiRedesignCost(editableComponentsMap);
    setRedesignCost(updatedCost);
  };

  const handleRegenerateContent = async (prompt: string, selectedPromptOptionValues: SelectedPromptOptionValues, editableComponentsMap?: EditableComponentsMap): Promise<void> => {
    const componentsMap = editableComponentsMap ?? getEditableComponentsMap();
    await regenerateContent(prompt, selectedPromptOptionValues, componentsMap, remainingCredits);
  };

  useEffect(() => {
    if (aiSubtitleState === AiSubtitleState.IDLE) {
      setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.NONE);
    }
  }, [aiSubtitleState]);

  useEffect(() => {
    if (currentlyOpenedSubPanel === PosterEditorDesignSubPanels.ANIMATION_STYLES) {
      const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
      if (!currentPage || Object.keys(currentPage.items.itemsHashMap).length === 0) {
        setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.NONE);
      }
    }
  }, [currentPageId]);

  useEffect(() => {
    if (currentlyOpenedSubPanel === PosterEditorDesignSubPanels.BACKGROUND_IMAGE) {
      if (currentPageBackground?.details.type === BackgroundTypeName.VIDEO) {
        setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.BACKGROUND_VIDEO);
      } else if (currentPageBackground?.details.type !== BackgroundTypeName.IMAGE) {
        setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.NONE);
      }
    }
    if (currentlyOpenedSubPanel === PosterEditorDesignSubPanels.BACKGROUND_VIDEO) {
      if (currentPageBackground?.details.type === BackgroundTypeName.IMAGE) {
        setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.BACKGROUND_IMAGE);
      } else if (currentPageBackground?.details.type !== BackgroundTypeName.VIDEO) {
        setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.NONE);
      }
    }
    if (
      currentlyOpenedSubPanel === PosterEditorDesignSubPanels.BACKGROUND_COLOR &&
      (!doesBackgroundHaveColor() || currentPageBackground?.details.type === BackgroundTypeName.IMAGE)
    ) {
      setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.NONE);
    }
  }, [currentPageBackgroundType]);

  const doesBackgroundHaveColor = (): boolean => {
    if (currentPageBackground?.details.type === BackgroundTypeName.IMAGE || currentPageBackground?.details.type === BackgroundTypeName.VIDEO) {
      return false;
    }

    return currentPageBackground?.details.fill.fillType !== FillTypes.NONE;
  };

  const getSubPanels = (): ReactElement<any> => {
    switch (currentlyOpenedSubPanel) {
      case PosterEditorDesignSubPanels.BACKGROUND_COLOR:
        return <PageBackgroundColorPanel />;
      case PosterEditorDesignSubPanels.BACKGROUND_IMAGE:
        return <ImageBackgroundOptions onClickColorFill={setCurrentlyOpenedSubPanel} />;
      case PosterEditorDesignSubPanels.BACKGROUND_VIDEO:
        return <VideoBackgroundOptions onClickColorFill={setCurrentlyOpenedSubPanel} />;
      case PosterEditorDesignSubPanels.BACKGROUND_IMAGE_TINT_COLOR:
        return <ImageBgTintColorPanel />;
      case PosterEditorDesignSubPanels.BACKGROUND_IMAGE_MULTIPLY_COLOR:
        return <ImageBgMultiplyColorPanel />;
      case PosterEditorDesignSubPanels.BACKGROUND_VIDEO_TINT_COLOR:
        return <VideoBgTintColorPanel />;
      case PosterEditorDesignSubPanels.BACKGROUND_VIDEO_MULTIPLY_COLOR:
        return <VideoBgMultiplyColorPanel />;
      case PosterEditorDesignSubPanels.ANIMATION_STYLES:
        return <PageAnimationPanel />;
      case PosterEditorDesignSubPanels.AI_SUBTITLES:
        return (
          <AiSubtitlePanel
            isOpenedInContextualMenu
            onDone={() => {
              setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.NONE);
            }}
            onBack={() => {
              setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.NONE);
            }}
            contentClasses={styles.content}
            hideHeader
          />
        );
      case PosterEditorDesignSubPanels.COLOR_PALETTE_STYLES:
        return <PageColorPaletteStylesPanelContent />;
      default:
        throw new Error('unhandled color panel option for default poster editing options');
    }
  };

  const handleBackButtonForHeader = (): void => {
    if (
      currentlyOpenedSubPanel === PosterEditorDesignSubPanels.BACKGROUND_IMAGE_TINT_COLOR ||
      currentlyOpenedSubPanel === PosterEditorDesignSubPanels.BACKGROUND_IMAGE_MULTIPLY_COLOR
    ) {
      setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.BACKGROUND_IMAGE);
      return;
    }
    if (
      currentlyOpenedSubPanel === PosterEditorDesignSubPanels.BACKGROUND_VIDEO_TINT_COLOR ||
      currentlyOpenedSubPanel === PosterEditorDesignSubPanels.BACKGROUND_VIDEO_MULTIPLY_COLOR
    ) {
      setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.BACKGROUND_VIDEO);
      return;
    }
    setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.NONE);
  };

  const getHeader = (): ReactElement<any> => {
    if (totalNumberOfPages > 1 && !isSubPanelOpened()) {
      return (
        <ContextualMenuHeader id="page-multipage-header">
          <ContextualMenuTextHeader
            text={window.i18next.t('pmwjs_page_number_out_of', {
              pageNumber: (currentPageOrder ?? 0) + 1,
              totalPages: totalNumberOfPages,
            })}
          />
        </ContextualMenuHeader>
      );
    }

    let text = window.i18next.t('pmwjs_design');

    if (isSubPanelOpened()) {
      if (currentlyOpenedSubPanel === PosterEditorDesignSubPanels.AI_SUBTITLES) {
        text = window.i18next.t('pmwjs_ai_subtitles');
      } else if (currentlyOpenedSubPanel === PosterEditorDesignSubPanels.ANIMATION_STYLES) {
        text = window.i18next.t('pmwjs_animation');
      } else if (currentlyOpenedSubPanel === PosterEditorDesignSubPanels.COLOR_PALETTE_STYLES) {
        text = window.i18next.t('pmwjs_styles');
      } else {
        text = window.i18next.t('pmwjs_color');
      }
    }

    if (currentlyOpenedSubPanel === PosterEditorDesignSubPanels.BACKGROUND_IMAGE) {
      text = window.i18next.t('pmwjs_background_image');
    }
    if (currentlyOpenedSubPanel === PosterEditorDesignSubPanels.BACKGROUND_VIDEO) {
      text = window.i18next.t('pmwjs_background_video');
    }
    return (
      <ContextualMenuHeader id="default-options-header">
        <ContextualMenuTextHeader
          showBackButton={isSubPanelOpened()}
          onBackButton={(): void => {
            handleBackButtonForHeader();
          }}
          text={text}
          className={currentlyOpenedSubPanel === PosterEditorDesignSubPanels.AI_SUBTITLES ? 'content-ai' : ''}
        />
      </ContextualMenuHeader>
    );
  };

  const isSubPanelOpened = (): boolean => {
    return currentlyOpenedSubPanel !== PosterEditorDesignSubPanels.NONE;
  };

  const getSuperUserOptions = (): ReactElement<any> | null => {
    if (window.posterEditor?.config?.showSuperUserOptions || window.PMW.isStorybook()) {
      return (
        <>
          <ContextualMenuHeadingOption
            id="default-super-user-options-heading"
            text={window.i18next.t('pmwjs_super_user_options')}
            pillContent={{
              type: PILL_CONTENT_TYPE.TEXT,
              text: window.i18next.t('pmwjs_admin_only'),
              textSize: TextSize.XXXSMALL,
              textColor: 'content-body-white',
            }}
          />
          <SuperUserPosterControls />
        </>
      );
    }

    return null;
  };

  // Leaving this here incase :return-of-the-king:
  const getAiRewritePromptBox = (): ReactElement => {
    const currentEditableComponentsMap = getEditableComponentsMap();

    return (
      <AiRedesignPromptBox
        startsFromIcon
        showInitialAnimation
        showRefreshSuggestions
        moodType={MoodType.REWRITE}
        regenerateContent={handleRegenerateContent}
        placeholder={window.i18next.t('pmwjs_update_text_for_birthday')}
        editableComponentsMap={currentEditableComponentsMap}
        promptOptionsForEditableComponents={getPromptOptionsForEditableComponents(currentEditableComponentsMap)}
        promptSuggestions={PromptSuggestions.DESIGN}
        headerContent={getHeaderContent()}
        recentPrompt={getPromptBoxPreviousPrompt}
        onPromptSuggestionSelected={onAiRedesignPromptSuggestionSelected}
        onRecentPromptSelected={onAiRedesignRecentPromptSelected}
        creditsPillContent={{
          remainingCredits,
          actionCost: redesignCost,
        }}
        onEditableComponentsMapChange={onEditableComponentsMapChange}
        unselectableEditableComponents={{
          [EditableComponent.IMAGE]: window.i18next.t('pmwjs_coming_soon_exclamation'),
          [EditableComponent.TEXT]: window.i18next.t('pmwjs_required'),
        }}
        upsellDialog={{
          dialogName: window.PMW.PREMIUM_ONLY_FEATURE_NAME_DESIGN_EDITOR_AI_REWRITE as string,
          showDialog: !isUserPremium(),
        }}
        onStartingIconClicked={onAiRedesignIconClicked}
        onContextualMenuOpened={onAiRedesignContextualMenuOpened}
        onPromptOptionChanged={onAiRedesignPromptOptionChanged}
      />
    );
  };

  const getOptions = (): ReactElement<any> => {
    if (isSubPanelOpened()) {
      return getSubPanels();
    }

    return (
      <>
        <PageUnlockAllItemsOption />
        <PageTypeOption />
        <PageBackgroundOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.BACKGROUND_COLOR);
          }}
          onClickEditImageBackground={(): void => {
            setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.BACKGROUND_IMAGE);
          }}
          onClickEditVideoBackground={(): void => {
            setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.BACKGROUND_VIDEO);
          }}
        />
        <ColorPaletteStylesOption
          onClick={(): void => {
            setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.COLOR_PALETTE_STYLES);
          }}
        />
        <PosterGenerateAiSubtitlesOption
          onClickGenerate={() => {
            setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.AI_SUBTITLES);
          }}
        />
        <PageAnimationOption
          onAnimationTypeClick={(): void => {
            setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.ANIMATION_STYLES);
          }}
        />
        <ContextualMenuHeadingOption id="default-title-heading" text={window.i18next.t('pmwjs_title')} />
        <DesignNameOption />
        <ContextualMenuHeadingOption id="default-layout-heading" text={window.i18next.t('pmwjs_layout')} />
        <PageGridOption />
        <PageFoldsOption />
        <PageBleedOption />
        <PageAlignmentGuidesOptions />
        <DesignerAttribution />
        {getSuperUserOptions()}
      </>
    );
  };

  const onOutsideClick = (): void => {
    if (isSubPanelOpened()) {
      setCurrentlyOpenedSubPanel(PosterEditorDesignSubPanels.NONE);
    }
  };

  const getKey = (): string => {
    if (!isSubPanelOpened()) {
      return ContextualMenuContentKeys.DEFAULT;
    }

    return '';
  };

  const getContentClassName = (): string => {
    if (currentlyOpenedSubPanel === PosterEditorDesignSubPanels.COLOR_PALETTE_STYLES) {
      return 'spacing-p-t-0';
    }

    if (currentlyOpenedSubPanel === PosterEditorDesignSubPanels.BACKGROUND_COLOR) {
      return 'spacing-p-b-0';
    }
    return '';
  };

  const getDefaultSinglePageOptionsAndSubPanels = (): ReactElement => {
    return (
      <ContextualMenu
        contentClassName={getContentClassName()}
        id="poster-editing-side-panel"
        onOutsideClick={onOutsideClick}
        includeLeftRightPadding={
          !isSubPanelOpened() ||
          currentlyOpenedSubPanel === PosterEditorDesignSubPanels.BACKGROUND_IMAGE ||
          currentlyOpenedSubPanel === PosterEditorDesignSubPanels.BACKGROUND_VIDEO ||
          currentlyOpenedSubPanel === PosterEditorDesignSubPanels.COLOR_PALETTE_STYLES
        }
        header={getHeader()}
        fireOnOutsideClickOnCanvasClick={false}
        contentKey={getKey()}
      >
        {getOptions()}
      </ContextualMenu>
    );
  };

  return getDefaultSinglePageOptionsAndSubPanels();
}

export default PosterEditingDefaultOptions;
